import { Button, DialogContentDrawerRenderProps, Dialog, Group } from '@economic/taco';
import React from 'react';
import { BulkBookAndSendViewData, BulkBookAndSendFormData } from '../types';
import { t } from '../../../../../../shared/components/l10n/l10n';
import { DisplayMessages } from '../../../shared/components/DisplayMessages';
import { Field } from '../../../../../../shared/components/Form';
import { BookOnlyConfirmationDialogContent } from '../BookOnlyConfirmationDialogContent';
import { useField } from 'react-final-form';
import { SendAsEInvoice, SendAsEmail } from '../../../shared/components/SendBy';
import { EmailOptionsDrawerToggle } from '../../../shared/components/EmailOptionsDrawerToggle';
import { FormApi } from 'final-form';
import { SendEmailOptionsDrawer, FullAttachmentOptions } from '../../../shared/components/SendEmailOptionsDrawer';
import { SalesDialogForm } from '../../../shared/components/SalesDialogForm';

export interface BulkBookDraftInvoicesDialogProps {
    loading: boolean;
    onHide: () => void;
    onSubmit: (formData: BulkBookAndSendFormData) => void;
    viewData?: BulkBookAndSendViewData;
    formData?: BulkBookAndSendFormData;
    error: boolean;
    open?: boolean;
    defaultOpen?: boolean;
}

export function BulkBookDraftInvoicesDialog({
    loading,
    onHide,
    onSubmit,
    viewData,
    formData,
    defaultOpen,
    open,
}: BulkBookDraftInvoicesDialogProps) {
    function onFormAction(form: FormApi, bookOnly: boolean) {
        form.change('bookOnly', bookOnly);
        form.submit();
    }

    return (
        <SalesDialogForm
            loading={loading}
            loadingSpinnerWithText
            ariaLabel={t`FORM_BULKBOOKANDSEND_TITLE`}
            defaultOpen={defaultOpen}
            open={open}
            className="book-and-send-dialog form h-[500px]"
            onHide={onHide}
            drawer={() => (
                <SendEmailOptionsDrawer hideRecipientsSection>
                    <FullAttachmentOptions />
                </SendEmailOptionsDrawer>
            )}
            initialData={formData}
            onSubmit={onSubmit}>
            {(form, drawer) =>
                viewData && (
                    <BulkBookDraftInvoicesDialogContent
                        viewData={viewData}
                        drawer={drawer}
                        onFormAction={bookOnly => onFormAction(form, bookOnly)}
                    />
                )
            }
        </SalesDialogForm>
    );
}

function BulkBookDraftInvoicesDialogContent({
    viewData,
    drawer,
    onFormAction,
}: {
    viewData: BulkBookAndSendViewData;
    drawer: DialogContentDrawerRenderProps;
    onFormAction: (bookOnly: boolean) => void;
}) {
    const [showBookOnlyConfirmation, setBookOnlyConfirmation] = React.useState<boolean>(false);
    const {
        input: { value: isChangeInvoiceDateChecked },
    } = useField<boolean>('changeInvoiceDate');

    if (showBookOnlyConfirmation) {
        return (
            <BookOnlyConfirmationDialogContent
                onBack={() => setBookOnlyConfirmation(false)}
                onOk={() => onFormAction(true)}
                text={t`FORM_BOOKANDSEND_BOOK_ONLY_CONFIRMATION_TEXT`}
            />
        );
    }

    function onBookOnlyAction() {
        if (viewData.skipBookOnlyConfirmation) {
            onFormAction(true);
        } else {
            if (drawer.open) {
                drawer.toggle();
            }

            setBookOnlyConfirmation(true);
        }
    }

    return (
        <div className="h-full flex flex-col">
            <Dialog.Title>{t`FORM_BULKBOOKANDSEND_TITLE`}</Dialog.Title>

            {viewData.sendElectronicallyCount > 0 && (
                <SendAsEInvoice
                    title={t('FORM_BULKBOOKANDSEND_ELECTRONIC_HEADER', { COUNT: viewData.sendElectronicallyCount })}
                    text={<em>{t`FORM_BULKBOOKANDSEND_ELECTRONIC_DESCRIPTION`}</em>}
                />
            )}

            {viewData.sendByEmailCount > 0 && (
                <>
                    <SendAsEmail title={t(`FORM_BULKBOOKANDSEND_EMAIL_HEADER`, { COUNT: viewData.sendByEmailCount })} />
                    <EmailOptionsDrawerToggle className="self-start" drawer={drawer} />
                </>
            )}

            <div className="book-and-send-dialog__messages">
                <DisplayMessages messages={viewData.nonBlockingWarnings} />
            </div>

            <div className="book-and-send-dialog__options">
                <div className="fields-container">
                    <Field.Checkbox
                        name="changeInvoiceDate"
                        text={t`FORM_BULKBOOKANDSEND_OPTIONS_CHANGEINVOICEDATETO`}
                        fieldClassName="field--no-height mr-4"
                    />
                    <div className="align-left">
                        <Field.DatePicker
                            name="newInvoiceDate"
                            disabled={!isChangeInvoiceDateChecked}
                            fieldClassName="field--no-height"
                        />
                    </div>
                </div>

                {viewData.canIncludePaymentLink && viewData.sendByEmailCount > 0 && (
                    <Field.Checkbox
                        name="includePaymentLink"
                        text={t`FORM_BOOKANDSEND_EMAIL_INCLUDE_PAYMENT_LINK`}
                        fieldClassName="field--no-height"
                    />
                )}

                <Field.Checkbox
                    name="showInvoicesAfterBooking"
                    text={t`FORM_BOOKANDSEND_SHOW_INVOICE`}
                    fieldClassName="field--no-height"
                />
            </div>

            <Dialog.Footer>
                <Group>
                    <Button onClick={onBookOnlyAction}>{t`FORM_BOOKANDSEND_BOOK_TEXT`}</Button>
                    <Button
                        appearance="primary"
                        onClick={() => onFormAction(false)}
                        autoFocus>{t`FORM_BOOKANDSEND_SEND_TEXT`}</Button>
                </Group>
            </Dialog.Footer>
        </div>
    );
}
